import { useLayoutEffect, useRef, useState } from 'react';
import { ICaseBattleGame } from '@/utils/types/ICaseBattle';
import { casebattlesStore } from '@/stores/casebattles.store';
import CaseImage from '@/components/CaseBattles/CaseImage';

interface Props {
    game: ICaseBattleGame;
}

// Geometry — one crate per round, fixed cell so the indicator offset and the
// lane auto-scroll are pure arithmetic (step = crate + gap), mirroring the
// reference (84px there with crate 80; here 54px with crate 48).
const CRATE = 48;
const GAP = 6;
const STEP = CRATE + GAP;
// While scrolling, hold the current crate this many cells from the left edge.
const LAG = 4;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const GameBattleRollingStrip = ({ game }: Props) => {
    const { getCase } = casebattlesStore((state) => state);

    const wrapRef = useRef<HTMLDivElement>(null);
    const [wrapW, setWrapW] = useState(0);

    // Track the visible width so we only auto-scroll when the strip overflows
    // — otherwise crates would slide off leaving empty space on the right.
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const measure = () => setWrapW(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const totalRounds = game.cases.length;
    // Completed rounds = rolls already streamed in for player 0 (same source
    // GameBattle uses for the rounds counter), so this updates live per roll.
    const openedCount = clamp(
        game.teams[0]?.participants[0]?.rolls?.length ?? 0,
        0,
        totalRounds
    );
    // The crate currently being opened. round is 1-based (openedCount + 1).
    const currentIndex = Math.min(openedCount, totalRounds - 1);
    const round = openedCount + 1;

    // Centre of the current crate within the (un-scrolled) lane.
    const indicatorX = currentIndex * STEP + CRATE / 2;

    // Auto-scroll the lane left, clamped so it never scrolls past the ends.
    const fullWidth = totalRounds * STEP - GAP;
    const maxScroll = Math.max(0, fullWidth - wrapW + 8 /* px padding */);
    const desired = (round - LAG) * STEP - GAP;
    const laneX = wrapW === 0 || maxScroll === 0 ? 0 : clamp(desired, 0, maxScroll);

    return (
        <div
            ref={wrapRef}
            className="relative w-full overflow-hidden bg-layer_2 border border-stroke rounded-fl px-1"
            style={{ height: CRATE + 12 }}
        >
            <div
                className="absolute top-1/2 left-1 -translate-y-1/2 flex items-center"
                style={{
                    gap: GAP,
                    height: CRATE,
                    transform: `translateY(-50%) translateX(-${laneX}px)`,
                    transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
                    willChange: 'transform',
                }}
            >
                {game.cases.map((c, i) => {
                    const item = getCase(c.caseId);
                    const opened = i < openedCount;
                    return (
                        <div
                            key={c.id}
                            title={item?.name}
                            className="relative shrink-0"
                            style={{
                                width: CRATE,
                                height: CRATE,
                                opacity: opened ? 0.5 : 1,
                                transform: opened ? 'scale(0.8)' : 'scale(1)',
                                transition: 'opacity 0.5s ease, transform 0.5s ease',
                            }}
                        >
                            {item && <CaseImage width={36} height={36} icon={item.icon} mini />}
                        </div>
                    );
                })}

                {/* Indicator — travels to the current crate with an easeInOutBack
                    overshoot. Lives inside the lane, so it scrolls with it and
                    stays pinned over the active crate. */}
                <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{
                        width: CRATE,
                        height: CRATE,
                        transform: `translateX(${indicatorX}px) translateX(-50%)`,
                        transition: 'transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                        willChange: 'transform',
                    }}
                >
                    <div
                        className="absolute inset-0 rounded-fl-sm"
                        style={{
                            border: '1.5px solid #ffb13d',
                            boxShadow:
                                '0 0 12px -2px rgba(255,177,61,0.8), inset 0 0 10px -4px rgba(255,177,61,0.6)',
                        }}
                    />
                    <span
                        className="absolute left-1/2 -translate-x-1/2"
                        style={{
                            top: -7,
                            width: 0,
                            height: 0,
                            borderLeft: '5px solid transparent',
                            borderRight: '5px solid transparent',
                            borderTop: '6px solid #ffb13d',
                            filter: 'drop-shadow(0 0 4px rgba(255,177,61,0.9))',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default GameBattleRollingStrip;
